"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import ScrollReveal from "@/components/common/ScrollReveal";
import { WEB_PENTEST_CENTER_SVG } from "./webPentestCenterSvg";
import { WEB_PENTEST_ORBIT_ICONS, type WebPentestOrbitIcon } from "./webPentestOrbitIcons";

/**
 * Placement of the floating category icons around the central scanner.
 * Positions and fade-in delays are ported verbatim from the v1 site
 * (assets/css/style.css → .web-pentest__item:nth-child(2..12)); icon1..icon11
 * map to nth-child(2..12) in v1's DOM order.
 *
 * There are no per-icon motion values any more. Every ring turns on the same
 * 36s linear cycle as the Compromise Assessment illustration — one shared
 * animation, no mismatched periods, no counter-rotating arcs, nothing else
 * moving. The icons bobbing, their ring dots breathing, the window sweep, the
 * floor ripple and the blinking findings markers were all removed on request.
 */
interface OrbitPlacement {
  top: string;
  left: string;
  /** Rendered width, as a share of the stage (v1 used percentages). */
  size: string;
  /** v1 fade-in delay. */
  delay: string;
}

const PLACEMENT: Record<string, OrbitPlacement> = {
  "web-pentest-icon1.svg": { top: "17%", left: "11%", size: "21.5%", delay: "0.2s" },
  "web-pentest-icon2.svg": { top: "13%", left: "33%", size: "12.4%", delay: "0s" },
  "web-pentest-icon3.svg": { top: "-2%", left: "42%", size: "16.5%", delay: "0.6s" },
  "web-pentest-icon4.svg": { top: "9%", left: "60%", size: "15.6%", delay: "0s" },
  "web-pentest-icon5.svg": { top: "15%", left: "77%", size: "18.5%", delay: "1s" },
  "web-pentest-icon6.svg": { top: "41%", left: "78%", size: "13.5%", delay: "0.4s" },
  "web-pentest-icon7.svg": { top: "56%", left: "78%", size: "16.3%", delay: "0.3s" },
  "web-pentest-icon8.svg": { top: "73%", left: "55%", size: "15%", delay: "0.7s" },
  "web-pentest-icon9.svg": { top: "60%", left: "39%", size: "11.3%", delay: "1.2s" },
  "web-pentest-icon10.svg": { top: "67%", left: "14%", size: "14.8%", delay: "1.4s" },
  "web-pentest-icon11.svg": { top: "48%", left: "17%", size: "14.5%", delay: "1s" },
};

const ORBIT: Array<WebPentestOrbitIcon & OrbitPlacement> = WEB_PENTEST_ORBIT_ICONS.map((icon) => ({
  ...icon,
  ...PLACEMENT[icon.file],
}));

export default function WebApplicationPenetrationTest() {
  const stageRef = useRef<HTMLDivElement>(null);
  // 22 ring layers plus the scanner's own v1 loops keep turning. Not worth
  // burning frames on while scrolled out of view, so the stage parks itself.
  const [running, setRunning] = useState(true);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const observer = new IntersectionObserver(([entry]) => setRunning(entry.isIntersecting), {
      threshold: 0,
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pb-16">
      {/* Copy sits at the top of the row, not centred against the illustration:
          the stage is ~145px taller than the three paragraphs, and v1's
          align-items:center pushed the first line down into the middle of the
          band, leaving a hole under the tabs. */}
      <div className="relative z-10 mx-auto flex w-full max-w-[1300px] flex-col items-start gap-10 pt-2 lg:flex-row lg:items-start lg:justify-between lg:gap-12">
        {/* Left: Body copy */}
        <div className="flex w-full max-w-[640px] flex-col gap-6 sm:gap-7 text-text-highlight">
          <ScrollReveal direction="up" delay={0}>
            <p className="font-[var(--font-avenir)] text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px] text-white">
              Web application vulnerabilities contribute significantly to the number of security
              incidents recorded each year thanks to the ease of exploitation and prevalence of
              automated hacking tools. Web application related security incidents have resulted in
              the theft of millions of credit cards, major financial and reputational damage for
              hundreds of enterprises, and even the compromise of thousands of computers that
              visited web sites hijacked by attackers.
            </p>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={80}>
            <p className="font-[var(--font-avenir)] text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px] text-white">
              ECQ provides Web Application Penetration Test service for organizations who wish to
              perform a thorough assessment of their web application and its architecture.
              ECQ&apos;s Consultants uses its in house RAPID Penetration Test framework, OWASP Top
              10, and OWASP Web Security Testing Guide to carry out all the activities required for
              this assessment.
            </p>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={160}>
            <p className="font-[var(--font-avenir)] text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px] text-white">
              Web Application Penetration Test service provided by ECQ covers the following 12
              categories.
            </p>
          </ScrollReveal>
        </div>

        {/* Right: Composite illustration — layered like v1 so the bug floats and
            the scan lines march; the orbit rings turn on top of that.
            The stage keeps its 609/565 ratio, so capping the wrapper width is
            what keeps the diagram from eating the phone viewport. */}
        <div className="relative flex w-full max-w-[320px] sm:max-w-[480px] lg:max-w-[609px] flex-shrink-0 items-center justify-center">
          <div
            ref={stageRef}
            className="wapt-stage relative mx-auto w-full"
            data-motion={running ? "on" : "off"}
          >
            {/* Central scanner. Inlined rather than loaded as an image so the
                #group-bug / #Stroke ids the animations target stay reachable. */}
            <div
              className="wapt-center"
              dangerouslySetInnerHTML={{ __html: WEB_PENTEST_CENTER_SVG }}
            />

            {/* Floating category icons, also inlined — page CSS cannot reach
                inside an <img>, and the ring layers have to be animated. */}
            {ORBIT.map((icon) => (
              <span
                key={icon.file}
                className="wapt-icon"
                aria-hidden="true"
                style={
                  {
                    top: icon.top,
                    left: icon.left,
                    width: icon.size,
                    aspectRatio: `${icon.width} / ${icon.height}`,
                    animationDelay: icon.delay,
                  } as CSSProperties
                }
                dangerouslySetInnerHTML={{ __html: icon.svg }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Motion. Only three loops run, and only the first is ours:
            waptSpin   : every orbit ring turns — the one effect asked for, on the
                         same 36s linear cycle as the Compromise Assessment rings
            waptBug    : continuous bug float inside the scanner (v1 SPIN_1)
            waptStroke : marching dashes on the scan lines (v1 STROKE)
          waptFadeIn is the v1 entrance, and it runs once. The icon drift, the
          breathing ring dots, the window sweep, the floor ripple and the blinking
          findings markers were removed: eleven icons each running four effects
          read as busy rather than alive, and the sweep and the blinks worked by
          making parts of the artwork disappear and come back.
          Selectors reach into markup injected with dangerouslySetInnerHTML, so
          they need :global — styled-jsx only rewrites what it can see in JSX. */}
      <style jsx>{`
        .wapt-stage {
          /* v1 aspect ratio for the illustration box */
          aspect-ratio: 609 / 565;
        }
        .wapt-center {
          position: absolute;
          top: 30%;
          left: 33%;
          width: 37.8%;
          z-index: 2;
        }
        .wapt-icon {
          position: absolute;
          z-index: 3;
          display: block;
          line-height: 0;
          opacity: 0;
          animation: waptFadeIn 1s linear both;
        }
        @keyframes waptFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        /* --- orbit icon rings ------------------------------------------------
           The grey ring and the red arc are concentric, so both turn about the
           same measured centre, which the extractor bakes onto each layer as an
           inline transform-origin. It is NOT the viewBox centre: anchor them to
           the middle of their own bounding box instead and each arc swings around
           its orbit rather than turning in place.
           Same cycle for both layers and for all eleven icons, matching the
           Compromise Assessment rings — the arc no longer runs the other way, so
           each pair reads as one ring turning.
           The dots do not turn: they sit on the ring but are their own root layer,
           and a swept circle would clear the viewBox and be shaved at the edge. */
        .wapt-icon :global(.wpo-ring),
        .wapt-icon :global(.wpo-arc) {
          transform-box: view-box;
          animation: waptSpin 36s linear infinite;
        }
        @keyframes waptSpin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* --- central scanner ------------------------------------------------ */
        .wapt-center :global(#group-bug) {
          animation: waptBug 2s infinite linear;
        }
        @keyframes waptBug {
          0% {
            transform: translate(10px, -5px);
          }
          25% {
            transform: translate(0px, -5px);
          }
          50% {
            transform: translate(-10px, 0px);
          }
          75% {
            transform: translate(0px, 10px);
          }
          100% {
            transform: translate(10px, -5px);
          }
        }
        .wapt-center :global(#Stroke-16),
        .wapt-center :global(#Stroke-17),
        .wapt-center :global(#Stroke-18),
        .wapt-center :global(#Stroke-19),
        .wapt-center :global(#Stroke-20),
        .wapt-center :global(#Stroke-21),
        .wapt-center :global(#Stroke-22),
        .wapt-center :global(#Stroke-23) {
          stroke-dasharray: 9px;
          stroke-dashoffset: 0;
          animation: waptStroke 1.2s infinite linear;
        }
        @keyframes waptStroke {
          from {
            stroke-dashoffset: 0px;
          }
          to {
            stroke-dashoffset: -50px;
          }
        }

        /* Parked while off-screen. !important is load-bearing, not laziness: the
           scanner rules above are keyed on ids from the v1 export (#Stroke-16,
           ...) and an id outranks any selector this switch could use, so without
           it the centre-SVG animations keep running while parked. */
        .wapt-stage[data-motion="off"] :global(*) {
          animation-play-state: paused !important;
        }

        @media (prefers-reduced-motion: reduce) {
          .wapt-icon {
            animation: none;
            opacity: 1;
          }
          .wapt-icon :global(.wpo-ring),
          .wapt-icon :global(.wpo-arc),
          .wapt-center :global(#group-bug),
          .wapt-center :global(#Stroke-16),
          .wapt-center :global(#Stroke-17),
          .wapt-center :global(#Stroke-18),
          .wapt-center :global(#Stroke-19),
          .wapt-center :global(#Stroke-20),
          .wapt-center :global(#Stroke-21),
          .wapt-center :global(#Stroke-22),
          .wapt-center :global(#Stroke-23) {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}
